/* VAXA — smooth in-page anchor scrolling.

   Clicking "Reviews" in the menu used to teleport the page ~15,000px in a
   single frame, and the menu panel stayed open on top of it, so you landed
   somewhere new behind a menu you still had to close yourself.

   Three jobs:
   1. Close the menu drawer first and release the scroll lock it puts on <body>,
      so the page is actually allowed to move.
   2. Glide to the target instead of jumping. Anything further than one screen
      cuts instantly to a short runway just before the target and eases only
      that last stretch. A genuinely animated 15,000px scroll is a strobe, not
      an animation, and it takes long enough that people scroll manually to
      escape it.
   3. Land the section below the sticky header rather than under it, and wake
      up the reveal-on-scroll animation of whatever we land in so the reviews
      are not still faded out when we arrive.

   Nothing here hijacks links to other pages. Only same-page hashes. */
(function () {
  if (window.__vxAnchor) return;
  window.__vxAnchor = true;

  var GAP      = 18;   // breathing room under the sticky header
  var LONG_HOP = 900;  // further than this and we cut most of the way first
  var RUNWAY   = 620;  // the stretch we actually animate on a long hop
  var DRAWER   = 240;  // Dawn's drawer slide-out is 200ms

  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function headerHeight() {
    var h = document.querySelector('sticky-header') ||
            document.querySelector('.header-wrapper') ||
            document.querySelector('.section-header');
    if (!h) return 0;
    var r = h.getBoundingClientRect();
    /* While the sticky header is in its hidden state its box is still there but
       translated off screen, so measure the height and not the position. */
    return r.height || 0;
  }

  function targetFor(hash) {
    if (!hash || hash === '#') return null;
    var id;
    try { id = decodeURIComponent(hash.slice(1)); } catch (e) { id = hash.slice(1); }
    if (!id) return null;
    return document.getElementById(id) || document.getElementsByName(id)[0] || null;
  }

  /* Anchor ids on the VAXA sections are zero-height marker spans nudged upwards
     with position:relative to fake a header offset. Measuring the span would
     bake that fudge in twice, so measure the box it sits in and do the offset
     honestly. */
  function boxFor(el) {
    var r = el.getBoundingClientRect();
    if (r.height === 0 && el.parentNode && el.parentNode.getBoundingClientRect) return el.parentNode;
    return el;
  }

  function destination(el) {
    var top = boxFor(el).getBoundingClientRect().top + window.scrollY - headerHeight() - GAP;
    var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    return Math.round(Math.min(Math.max(top, 0), max));
  }

  /* Sections that fade their contents in on scroll expose __vxRvObserve on
     their root. Poke every one between the target and <body> so the reveal has
     started before we get there. A plain scroll event covers anything that
     listens the ordinary way. */
  function prime(el) {
    var n = el;
    while (n && n !== document.body) {
      if (typeof n.__vxRvObserve === 'function') {
        try { n.__vxRvObserve(); } catch (e) {}
      }
      n = n.parentNode;
    }
    try { window.dispatchEvent(new Event('scroll')); } catch (e) {}
  }

  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  var raf = null;
  var abort = null;

  /* onMoved runs every time the page has been put somewhere new: after the
     opening cut, and again once the glide has settled. It has to run at both
     ends, because at the start of the runway the reviews are still below the
     fold and so nothing would reveal, and scroll events alone are not
     dependable enough to catch the rest. */
  function glideTo(to, onMoved) {
    if (raf) { cancelAnimationFrame(raf); raf = null; }
    if (abort) { abort(); abort = null; }

    var arrive = function () { if (onMoved) onMoved(); };

    var from = window.scrollY;
    if (Math.abs(to - from) < 2) { arrive(); return; }

    if (reduced()) {
      window.scrollTo(0, to);
      arrive();
      return;
    }

    if (Math.abs(to - from) > LONG_HOP) {
      from = to - (to > from ? RUNWAY : -RUNWAY);
      window.scrollTo(0, Math.max(0, from));
      from = window.scrollY;
    }

    arrive();

    var dist = to - from;
    var dur  = Math.min(820, Math.max(340, Math.abs(dist) * 0.95));
    var t0   = null;
    var dead = false;

    /* If the reader takes over with the wheel, a finger or the keyboard, get
       out of the way immediately. Fighting someone for the scroll position is
       the worst thing a script like this can do. */
    var bail = function () { dead = true; };
    window.addEventListener('wheel', bail, { passive: true });
    window.addEventListener('touchstart', bail, { passive: true });
    window.addEventListener('keydown', bail);
    var cleanup = function () {
      window.removeEventListener('wheel', bail);
      window.removeEventListener('touchstart', bail);
      window.removeEventListener('keydown', bail);
    };
    abort = function () { dead = true; cleanup(); };

    var settled = false;
    var step = function (ts) {
      if (dead || settled) { raf = null; cleanup(); return; }
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur);
      window.scrollTo(0, from + dist * easeOutCubic(p));
      if (p < 1) {
        raf = requestAnimationFrame(step);
      } else {
        settled = true;
        raf = null;
        cleanup();
        abort = null;
        arrive();
      }
    };
    raf = requestAnimationFrame(step);

    /* requestAnimationFrame is paused whenever the page is not being painted,
       and a paused animation here would strand the reader a few hundred pixels
       short of where they asked to go. If the frames never came, put them where
       they were going. */
    setTimeout(function () {
      if (settled || dead) return;
      settled = true;
      if (raf) { cancelAnimationFrame(raf); raf = null; }
      cleanup();
      abort = null;
      window.scrollTo(0, to);
      arrive();
    }, dur + 350);
  }

  /* Returns true if there was an open drawer to shut. */
  function closeDrawer() {
    var details = document.getElementById('Details-menu-drawer-container');
    if (!details || !details.hasAttribute('open')) return false;

    var summary = details.querySelector(':scope > summary');
    var drawer  = details.closest ? details.closest('header-drawer') : null;

    /* Dawn's own close, so the slide-out plays and the focus trap is released
       properly. */
    if (drawer && typeof drawer.closeMenuDrawer === 'function') {
      try { drawer.closeMenuDrawer(new Event('click'), summary); } catch (e) {}
    } else if (summary) {
      summary.click();
    }

    /* Dawn only drops the open attribute at the end of a 400ms
       requestAnimationFrame loop, and the body scroll lock is mirrored off that
       attribute, so the page would still be frozen when we tried to move it.
       Worse, those frames stop entirely if the page is not being painted, and
       then the drawer never closes at all. Shut it on our own clock; Dawn's
       loop lands on the same answer a moment later and the repeat is harmless. */
    setTimeout(function () {
      details.classList.remove('menu-opening');
      details.removeAttribute('open');
      document.body.classList.remove('vx-drawer-open');
      document.body.classList.remove('overflow-hidden-mobile');
      document.body.classList.remove('overflow-hidden-tablet');
    }, DRAWER - 40);

    return true;
  }

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!e.target || !e.target.closest) return;

    var a = e.target.closest('a[href]');
    if (!a || (a.target && a.target !== '_self')) return;
    if (a.hasAttribute('download')) return;

    var url;
    try { url = new URL(a.href, location.href); } catch (err) { return; }
    if (url.origin !== location.origin) return;
    if (url.pathname !== location.pathname) return;   // a real navigation, leave it alone
    if (url.search !== location.search) return;
    if (!url.hash || url.hash === '#') return;

    var el = targetFor(url.hash);
    if (!el) return;

    e.preventDefault();

    var waited = closeDrawer() ? DRAWER : 0;
    setTimeout(function () {
      glideTo(destination(el), function () { prime(el); });
      /* One last sweep after everything has come to rest, for the cards that
         only entered the viewport on the final few pixels. */
      setTimeout(function () { prime(el); }, 1200);
      if (history.replaceState) {
        try { history.replaceState(null, '', url.hash); } catch (err) {}
      }
    }, waited);
  }, false);

  /* Arriving from another page. The browser has already hard-jumped by the time
     anything runs, and it jumped to the wrong place because the sticky header
     covers the top of the section. Re-seat it, then repeat a couple of times as
     late images and web fonts shift the page under us. Any real scrolling by
     the reader cancels the rest. */
  (function landing() {
    if (!location.hash) return;
    if (!targetFor(location.hash)) return;

    var touched = false;
    var give = function () { touched = true; };
    window.addEventListener('wheel', give, { passive: true });
    window.addEventListener('touchstart', give, { passive: true });
    window.addEventListener('keydown', give);

    var seat = function () {
      if (touched) return;
      var el = targetFor(location.hash);
      if (!el) return;
      window.scrollTo(0, destination(el));
      prime(el);
    };

    seat();
    setTimeout(seat, 250);
    setTimeout(seat, 800);
    window.addEventListener('load', function () { setTimeout(seat, 80); });
    setTimeout(function () {
      window.removeEventListener('wheel', give);
      window.removeEventListener('touchstart', give);
      window.removeEventListener('keydown', give);
    }, 1600);
  })();
})();
